"use client";

import { useQuery } from "@tanstack/react-query";

import type { UsePrimaryNamesParameters, WithEnsNodeProviderOptions } from "../types";
import { createPrimaryNamesQueryOptions } from "../utils/query";
import { useEnsNodeProviderOptions } from "./useEnsNodeProviderOptions";

/**
 * Resolves the primary names of a specified address across multiple chains.
 *
 * Each returned Primary Name, if set, is guaranteed to be a normalized name.
 * If the primary name set for the address on any chain is not normalized, `null` is returned for that chain as if no primary name was set.
 *
 * @param parameters - Configuration for the address resolution
 * @returns Query result with resolved primary names
 *
 * @example
 * ```typescript
 * import { usePrimaryNames } from "@ensnode/ensnode-react";
 *
 * function DisplayPrimaryNames() {
 *   const { data, isLoading, error } = usePrimaryNames({
 *     address: "0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045",
 *   });
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       {Object.entries(data.names).map(([chainId, name]) => (
 *         <div key={chainId}>
 *           <h3>Primary Name (Chain Id: {chainId})</h3>
 *           <p>{name}</p>
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePrimaryNames(
  parameters: UsePrimaryNamesParameters & WithEnsNodeProviderOptions,
) {
  const { options, query = {}, address, ...args } = parameters;
  const providerOptions = useEnsNodeProviderOptions(options);

  const canEnable = address !== null;

  const queryOptions = canEnable
    ? createPrimaryNamesQueryOptions(providerOptions, { ...args, address })
    : { enabled: false, queryKey: ["disabled"] as const };

  return useQuery({
    ...queryOptions,
    ...query,
    enabled: canEnable && (query.enabled ?? queryOptions.enabled),
  });
}
